
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import AnimatedCalls from './components/AnimatedCalls';

export default function DemoPage() {
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position to toggle navbar style
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure page starts at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 transition-colors cursor-pointer hover:text-purple-600"
            >
              Home
            </Link>
            <Link
              to="/#features"
              className="text-sm font-medium text-gray-700 transition-colors cursor-pointer hover:text-purple-600"
            >
              Features
            </Link>
            <Link
              to="/#pricing"
              className="text-sm font-medium text-gray-700 transition-colors cursor-pointer hover:text-purple-600"
            >
              Pricing
            </Link>
            <Link
              to="/start-free-trial"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Video Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mb-6">
            <i className="ri-play-circle-fill text-purple-600 text-lg"></i>
            <span className="text-sm font-medium text-purple-700">Product Demo</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            See AI Receptionist in Action
          </h1>
          <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            Watch how our AI receptionist handles real calls, books appointments, and
            transforms the way businesses communicate with their customers.
          </p>

          {/* Video Embed Container */}
          <div
            className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-900"
            style={{ paddingBottom: '56.25%' }}
          >
            <video
              className="absolute inset-0 w-full h-full object-contain"
              src="https://storage.googleapis.com/msgsndr/d83zAmez1OMBkqJ1O6SO/media/6995ebb1dde40b61f9e91554.mp4"
              title="AI Receptionist Demo"
              controls
              controlsList="nodownload"
            ></video>
          </div>
        </div>
      </section>

      {/* Animated Call Simulations */}
      <AnimatedCalls />

      {/* Call CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          ></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <i className="ri-phone-fill text-white text-4xl"></i>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Want to Try This Live<br />For Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Experience the power of AI receptionist firsthand. Give us a call and see how it
            works in real time — no commitment, just a quick live demo tailored to your
            business.
          </p>

          <div className="bg-white/15 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/20 max-w-lg mx-auto">
            <p className="text-white/80 text-sm font-medium uppercase tracking-widest mb-3">
              Call Us Now
            </p>
            <a
              href="tel:+18454424515"
              className="text-4xl md:text-5xl font-bold text-white hover:text-pink-200 transition-colors cursor-pointer"
            >
              (845) 442-4515
            </a>
            <p className="text-white/70 text-sm mt-4">
              Available 24/7 — Our AI will answer!
            </p>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/start-free-trial"
              className="px-8 py-4 bg-white text-purple-700 font-semibold rounded-full hover:shadow-2xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Or Start Free Trial
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
            <Link
              to="/"
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
